import { useTranslation } from "react-i18next";
import { IPlaceInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import { IZoneInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import { RemoteApiError } from "../useRemote/types";
import { useMutation, useQuery, useQueryClient, UseMutationResult } from "react-query";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useRemoteMutation } from "../useRemote/useRemoteMutation";

export function useRemotePlaceCreateInventory() {
  const { t } = useTranslation();

  return useRemoteMutation<IPlaceInventory, RemoteApiError, IPlaceInventory>({
    url: `/inventory/place/`,
    key: ["inventory", "place"],
    method: "post",
    snackbarTextOnFail: t(`Failed to create the Place inventory`)
  });
}

export function useRemotePlacePatchInventory({
  placeInventoryId
}: {
  placeInventoryId: number | undefined;
}) {
  const { t } = useTranslation();

  return useRemoteMutation<void, RemoteApiError, IPlaceInventory>({
    url: `/inventory/place/${placeInventoryId}`,
    key: ["inventory", "place"],
    method: "patch",
    snackbarTextOnFail: t(`Failed to update place inventory with id ${placeInventoryId}`)
  });
}

export function useRemoteZoneCreateInventory() {
  const { t } = useTranslation();

  return useRemoteMutation<IZoneInventory, RemoteApiError, IZoneInventory>({
    url: `/inventory/zone/`,
    key: ["inventory", "zone"],
    method: "post",
    snackbarTextOnFail: t(`Failed to create the zone inventory`)
  });
}

interface UpdateZoneInventoryDto {
  picturePaths?: string[];
  name?: string;
  zoneTypeId?: number;
}

export interface PatchZoneInventoryVariables {
  zoneInventoryId: number;
  dto: UpdateZoneInventoryDto;
  files?: File[];
}

export function useRemoteZonePatchInventory(): UseMutationResult<
  IZoneInventory,
  RemoteApiError,
  PatchZoneInventoryVariables
> {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<IZoneInventory, RemoteApiError, PatchZoneInventoryVariables>({
    mutationFn: async variables => {
      const formData = new FormData();
      const { dto } = variables;

      if (dto.name) formData.append("name", dto.name);
      if (dto.zoneTypeId) formData.append("zoneTypeId", dto.zoneTypeId.toString());
      if (dto.picturePaths) {
        dto.picturePaths.forEach(path => formData.append("picturePaths[]", path));
      }

      variables.files?.forEach(file => {
        formData.append("files", file, file.name);
      });

      const { data } = await axios.patch<IZoneInventory>(
        `/inventory/zone/${variables.zoneInventoryId}`,
        formData
      );
      return data;
    },
    mutationKey: ["inventory", "zone"],
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory", "zone"]);
    },
    onError: (error, variables) => {
      enqueueSnackbar(t(`Failed to update zone inventory with id ${variables.zoneInventoryId}`), {
        variant: "error"
      });
    }
  });
}

export function useRemoteFetchZoneInventory(
  zoneInventoryId: number | undefined,
  options?: { enabled?: boolean }
) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useQuery<IZoneInventory, RemoteApiError>(
    ["inventory", "zone", zoneInventoryId],
    async () => {
      const { data } = await axios.get<IZoneInventory>(`/inventory/zone/${zoneInventoryId}`);
      return data;
    },
    {
      enabled: !!zoneInventoryId && (options?.enabled ?? false),
      onError: () => {
        enqueueSnackbar(t("Failed to fetch zone inventory"), { variant: "error" });
      }
    }
  );
}
